import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { getAnalysisJobStatus, getAnalysisJobResults } from '../api/api';
import { AnalysisJobStatus, AnalysisJobStatusDisplay } from '../utils/analysisConstants';
import HistogramChart from '../components/analysis/HistogramChart';

// Placeholder for result renderers - these will be separate components
const ResultTable = ({ title, data }) => (
  <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
    <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
    {data && data.columns && data.rows ? (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-borderDefault">
          <thead className="bg-backgroundOffset">
            <tr>
              {data.columns.map(col => (
                <th key={col.key} scope="col" className="px-4 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-backgroundSurface divide-y divide-borderDefault">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {data.columns.map(col => (
                  <td key={col.key} className="px-4 py-2 whitespace-nowrap text-sm text-textPrimary">
                    {typeof row[col.key] === 'number' ? row[col.key].toFixed(2) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : <p className="text-textSecondary">Table data is not available or malformed.</p>}
  </div>
);

const ResultKeyValuePairs = ({ title, data }) => (
  <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
    <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
    {data && Array.isArray(data) ? (
      <ul className="space-y-1">
        {data.map(pair => (
          <li key={pair.key} className="text-sm">
            <span className="font-medium text-textSecondary">{pair.key}: </span>
            <span className="text-textPrimary">{pair.value}</span>
          </li>
        ))}
      </ul>
    ) : <p className="text-textSecondary">Key-value data is not available or malformed.</p>}
  </div>
);

const ResultImageSVG = ({ title, data }) => (
  <div className="mt-4 p-3 bg-backgroundBody rounded shadow">
    <h3 className="text-lg font-semibold mb-2 text-textPrimary">{title}</h3>
    {data && data.svg_content ? (
      <div className="overflow-x-auto" dangerouslySetInnerHTML={{ __html: data.svg_content }} />
    ) : <p className="text-textSecondary">SVG image is not available or malformed.</p>}
  </div>
);

const AnalysisResultRenderer = ({ resultData }) => { // Changed prop name from components to resultData
  if (!resultData) {
    return <p className="text-textSecondary">No result data available.</p>;
  }

  // Case 1: result_data has a 'components' array (original design)
  if (resultData.components && Array.isArray(resultData.components) && resultData.components.length > 0) {
    return (
      <div>
        {resultData.components.map((component, index) => {
          switch (component.type) {
            case 'table':
              return <ResultTable key={index} title={component.title} data={component.data} />;
            case 'key_value_pairs':
              return <ResultKeyValuePairs key={index} title={component.title} data={component.data} />;
            case 'image_svg':
              return <ResultImageSVG key={index} title={component.title} data={component.data} />;
            case 'histogram':
              return <HistogramChart key={index} title={component.title} data={component.data} />;
            default:
              return <p key={index} className="text-textSecondary">Unsupported component type: {component.type}</p>;
          }
        })}
      </div>
    );
  }
  
  // Case 2: result_data is a flat object (like the TREND_CONTINUATION example)
  // We can transform its direct properties into key-value pairs for display.
  const flatDataKeys = Object.keys(resultData);
  if (flatDataKeys.length > 0 && !resultData.components) { // Check if it's not empty and doesn't have components
    // Exclude 'analysis_display_name' and 'summary' if they exist, as they might be handled separately or are titles.
    // Or, include them if they are part of the data to be displayed as key-value.
    // For the provided example, all keys seem like data points.
    const keyValuePairs = flatDataKeys.map(key => ({
      key: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '), // Prettify key
      value: typeof resultData[key] === 'number' ? resultData[key].toFixed(2) : resultData[key].toString()
    }));

    // You might want a specific title for this kind of flat data display
    const title = resultData.analysis_display_name || "Analysis Data"; 
    
    return <ResultKeyValuePairs title={title} data={keyValuePairs} />;
  }

  // Fallback if result_data is present but doesn't fit known structures
  return <p className="text-textSecondary">Result data is present but could not be displayed in a recognized format.</p>;
};


const JOB_DETAILS_POLL_INTERVAL = 3000; // Poll more frequently for an active job view

const AnalysisJobDetailsPage = () => {
  const { jobId } = useParams();
  const [jobStatus, setJobStatus] = useState(null);
  const [jobResults, setJobResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchJobData = useCallback(async () => {
    try {
      setIsLoading(true);
      const statusData = await getAnalysisJobStatus(jobId);
      setJobStatus(statusData);

      if (statusData.status === AnalysisJobStatus.COMPLETED_SUCCESS) {
        const resultsData = await getAnalysisJobResults(jobId);
        setJobResults(resultsData);
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch job details:", err);
      setError(err.detail || err.message || "Failed to load job details.");
      // Keep existing data if partial load, or clear if total failure
      // setJobStatus(null); 
      // setJobResults(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Effect for initial fetch when component mounts or jobId changes
  useEffect(() => {
    fetchJobData();
  }, [jobId, fetchJobData]); // fetchJobData is stable due to useCallback

  // Determine if the job is active (i.e., not completed or failed)
  const isJobActive = jobStatus &&
                      jobStatus.status !== AnalysisJobStatus.COMPLETED_SUCCESS &&
                      jobStatus.status !== AnalysisJobStatus.COMPLETED_FAIL;

  // Effect for managing the polling interval
  useEffect(() => {
    let intervalId = null;
    if (isJobActive) {
      // Start polling if the job is active
      intervalId = setInterval(fetchJobData, JOB_DETAILS_POLL_INTERVAL);
    }
    
    // Cleanup function to clear the interval when the component unmounts
    // or when isJobActive or fetchJobData changes (though fetchJobData should be stable)
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isJobActive, fetchJobData]); // Dependencies: isJobActive (boolean) and fetchJobData (stable callback)


  if (isLoading && !jobStatus) { // Show loading only on initial load
    return <PageLayout title="Loading Job Details..."><p className="text-center text-textSecondary">Fetching data...</p></PageLayout>;
  }

  if (error && !jobStatus) { // Show error if initial load failed completely
    return <PageLayout title="Error"><p className="text-red-500 text-center">{error}</p></PageLayout>;
  }
  
  if (!jobStatus) { // Should ideally be covered by loading/error, but as a fallback
    return <PageLayout title="Job Not Found"><p className="text-center text-textSecondary">Could not retrieve job information.</p></PageLayout>;
  }

  return (
    <PageLayout 
      title={`Analysis Job: ${jobStatus.job_id.substring(0,8)}...`}
      backButton={{ label: 'Back to Analysis', fallbackTo: '/analysis' }}
    >
      {error && <p className="mb-4 text-red-500 bg-red-100 p-3 rounded-md">Error updating job: {error}</p>}
      
      <section className="mb-6 p-4 bg-backgroundSurface rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-3 text-textPrimary">Job Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><strong>Job ID:</strong> {jobStatus.job_id}</div>
          <div><strong>Status:</strong> 
            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
              jobStatus.status === AnalysisJobStatus.COMPLETED_SUCCESS ? 'bg-green-100 text-green-700' :
              jobStatus.status === AnalysisJobStatus.COMPLETED_FAIL ? 'bg-red-100 text-red-700' :
              jobStatus.status === AnalysisJobStatus.RUNNING_ANALYSIS ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {AnalysisJobStatusDisplay[jobStatus.status] || jobStatus.status}
            </span>
          </div>
          <div><strong>Analysis Type:</strong> {jobStatus.analysis_type.replace(/_/g, ' ')}</div>
          <div><strong>Instrument:</strong> {jobStatus.instrument_symbol}</div>
          <div><strong>Target Timeframe:</strong> {jobStatus.target_timeframe}</div>
          <div><strong>Created At:</strong> {new Date(jobStatus.created_at).toLocaleString()}</div>
          <div><strong>Last Updated:</strong> {new Date(jobStatus.updated_at).toLocaleString()}</div>
        </div>
        {jobStatus.status === AnalysisJobStatus.COMPLETED_FAIL && jobStatus.error_message && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
            <h3 className="text-md font-semibold text-red-700">Error Details:</h3>
            <p className="text-sm text-red-600 whitespace-pre-wrap">{jobStatus.error_message}</p>
          </div>
        )}
      </section>

      {jobStatus.status === AnalysisJobStatus.COMPLETED_SUCCESS && jobResults && (
        <section className="p-4 bg-backgroundSurface rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-3 text-textPrimary">Analysis Results</h2>
          <p className="text-sm text-textSecondary mb-1">Generated At: {new Date(jobResults.generated_at).toLocaleString()}</p>
          <AnalysisResultRenderer resultData={jobResults.result_data} /> 
        </section>
      )}

      {isLoading && jobStatus && <p className="text-center text-textSecondary mt-4">Updating job status...</p>}

      {(jobStatus.status !== AnalysisJobStatus.COMPLETED_SUCCESS && jobStatus.status !== AnalysisJobStatus.COMPLETED_FAIL && !jobResults) && (
         <section className="mt-6 p-4 bg-backgroundSurface rounded-lg shadow text-center">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Results Pending</h2>
            <p className="text-textSecondary">Analysis is currently in progress. Status: {AnalysisJobStatusDisplay[jobStatus.status] || jobStatus.status}</p>
            <p className="text-textSecondary mt-2">This page will automatically update.</p>
         </section>
      )}
    </PageLayout>
  );
};

export default AnalysisJobDetailsPage;
